import { Injectable } from '@angular/core';

export interface PerformanceMetric {
  name: string;
  value: number; // Duration in milliseconds
  timestamp: number;
  metadata?: Record<string, any>;
}

export interface ApiCallMetric extends PerformanceMetric {
  endpoint: string;
  method: string;
  statusCode?: number;
  success: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PerformanceMonitoringService {
  private metrics: PerformanceMetric[] = [];
  private readonly MAX_METRICS = 1000;
  
  // Performance budgets
  private readonly BUDGETS = {
    apiCall: 1000,        // 1 second for API calls
    pageLoad: 3000,       // 3 seconds for page load
    interaction: 100,     // 100ms for user interactions
    render: 16.67         // ~60fps (16.67ms per frame)
  };

  /**
   * Start performance measurement
   */
  startMeasure(name: string): (metadata?: Record<string, any>) => void {
    const startTime = performance.now();
    const startMark = `${name}-start`;

    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(startMark);
    }

    // Return function to end measurement
    return (metadata?: Record<string, any>) => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      const endMark = `${name}-end`;

      if (typeof performance !== 'undefined' && performance.mark) {
        performance.mark(endMark);
        performance.measure(name, startMark, endMark);
      }

      this.recordMetric({
        name,
        value: duration,
        timestamp: Date.now(),
        metadata
      });
    };
  }

  /**
   * Measure async operation
   */
  async measureAsync<T>(
    name: string,
    operation: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    const endMeasure = this.startMeasure(name);
    try {
      const result = await operation();
      endMeasure({ ...metadata, success: true });
      return result;
    } catch (error) {
      endMeasure({ ...metadata, success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Record API call metric
   */
  recordApiCall(metric: Omit<ApiCallMetric, 'name' | 'value' | 'timestamp'> & { duration: number }): void {
    const apiMetric: ApiCallMetric = {
      name: `api.${metric.method.toLowerCase()}.${metric.endpoint}`,
      value: metric.duration,
      timestamp: Date.now(),
      endpoint: metric.endpoint,
      method: metric.method,
      statusCode: metric.statusCode,
      success: metric.success,
      metadata: metric.metadata
    };

    this.recordMetric(apiMetric);

    // Check against budget
    if (metric.duration > this.BUDGETS.apiCall) {
      console.warn(`[Performance] API call exceeded budget: ${metric.endpoint} took ${metric.duration}ms`);
    }
  }

  /**
   * Record page load metric
   */
  recordPageLoad(route: string, loadTime: number): void {
    this.recordMetric({
      name: `page.load.${route}`,
      value: loadTime,
      timestamp: Date.now(),
      metadata: { route }
    });

    if (loadTime > this.BUDGETS.pageLoad) {
      console.warn(`[Performance] Page load exceeded budget: ${route} took ${loadTime}ms`);
    }
  }

  /**
   * Record user interaction metric
   */
  recordInteraction(action: string, duration: number, metadata?: Record<string, any>): void {
    this.recordMetric({
      name: `interaction.${action}`,
      value: duration,
      timestamp: Date.now(),
      metadata
    });

    if (duration > this.BUDGETS.interaction) {
      console.warn(`[Performance] Interaction exceeded budget: ${action} took ${duration}ms`);
    }
  }

  /**
   * Get performance statistics
   */
  getStats(metricName?: string): {
    count: number;
    average: number;
    min: number;
    max: number;
    p50: number;
    p95: number;
    p99: number;
  } {
    const relevantMetrics = metricName
      ? this.metrics.filter(m => m.name === metricName)
      : this.metrics;

    if (relevantMetrics.length === 0) {
      return { count: 0, average: 0, min: 0, max: 0, p50: 0, p95: 0, p99: 0 };
    }

    const values = relevantMetrics.map(m => m.value).sort((a, b) => a - b);
    const sum = values.reduce((a, b) => a + b, 0);

    return {
      count: values.length,
      average: sum / values.length,
      min: values[0],
      max: values[values.length - 1],
      p50: this.percentile(values, 50),
      p95: this.percentile(values, 95),
      p99: this.percentile(values, 99)
    };
  }

  /**
   * Get metrics by name pattern
   */
  getMetricsByName(pattern: string | RegExp): PerformanceMetric[] {
    const regex = pattern instanceof RegExp ? pattern : new RegExp(pattern);
    return this.metrics.filter(m => regex.test(m.name));
  }

  /**
   * Clear metrics
   */
  clearMetrics(): void {
    this.metrics = [];
    
    // Clear performance marks
    if (typeof performance !== 'undefined' && performance.clearMarks) {
      performance.clearMarks();
    }
    if (typeof performance !== 'undefined' && performance.clearMeasures) {
      performance.clearMeasures();
    }
  }

  /**
   * Get all metrics
   */
  getAllMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  /**
   * Record a metric
   */
  private recordMetric(metric: PerformanceMetric): void {
    this.metrics.push(metric);
    
    // Keep only recent metrics
    if (this.metrics.length > this.MAX_METRICS) {
      this.metrics.shift();
    }

    // Log in development
    if (!this.isProduction() && metric.value > this.BUDGETS.apiCall) {
    }
  }

  /**
   * Calculate percentile
   */
  private percentile(sortedValues: number[], p: number): number {
    if (sortedValues.length === 0) return 0;
    const index = Math.ceil((p / 100) * sortedValues.length) - 1;
    return sortedValues[Math.max(0, index)];
  }

  /**
   * Check if in production
   */
  private isProduction(): boolean {
    return typeof window !== 'undefined' && 
           (window.location.hostname.includes('web.app') || 
            window.location.hostname.includes('firebaseapp.com'));
  }
}
